import type { GraphQLError } from 'graphql';

import { writeQueryInterop } from '@confluence/apollo-relay-interop/entry-points/writeQuery.ts';
import { fg } from '@confluence/feature-gating';
import { query } from '@confluence/query-preloader-tools/entry-points/graphql.ts';

import graphqlHelpersDSPIsActionEnabledForContentQuery, {
	type graphqlHelpersDSPIsActionEnabledForContentQuery$data,
	type graphqlHelpersDSPIsActionEnabledForContentQuery$variables,
} from './__generated__/graphqlHelpersDSPIsActionEnabledForContentQuery.graphql.ts';

export type AttachmentDownloadDspAction = 'ATTACHMENT_DOWNLOAD' | 'PAGE_EXPORT';

type RouteOwnedDspData = {
	dataSecurityPolicy?: {
		isActionEnabledForContent?: {
			action?: string | null;
			allowed?: boolean | null;
		} | null;
	} | null;
};

type LegacyDspResult = NonNullable<
	NonNullable<
		graphqlHelpersDSPIsActionEnabledForContentQuery$data['dataSecurityPolicy']
	>['isActionEnabledForContent']
>;

export const getAttachmentDownloadDspAction = (): AttachmentDownloadDspAction =>
	fg('cc-dlp_split-attachment-dl-from-export-dsp') ? 'ATTACHMENT_DOWNLOAD' : 'PAGE_EXPORT';

/**
 * Seeds the legacy DSP operation cache from the route query, so the renderer keeps its existing
 * cache-first read without issuing a second policy request.
 */
export const primeIsAttachmentDownloadEnabledFromRouteData = async (
	contentId: string,
	spaceKey: string,
	action: AttachmentDownloadDspAction,
	data: RouteOwnedDspData | null | undefined,
): Promise<void> => {
	const isActionEnabledForContent: LegacyDspResult | null = data?.dataSecurityPolicy
		?.isActionEnabledForContent
		? {
				action,
				allowed: data.dataSecurityPolicy.isActionEnabledForContent.allowed || false,
			}
		: null;

	const cacheData: graphqlHelpersDSPIsActionEnabledForContentQuery$data = isActionEnabledForContent
		? {
				dataSecurityPolicy: {
					isActionEnabledForContent,
				},
			}
		: { dataSecurityPolicy: null };

	writeQueryInterop<
		graphqlHelpersDSPIsActionEnabledForContentQuery$data,
		graphqlHelpersDSPIsActionEnabledForContentQuery$variables
	>({
		query: graphqlHelpersDSPIsActionEnabledForContentQuery,
		variables: {
			action,
			contentId,
			contentStatus: 'CURRENT',
			contentVersion: 1,
			spaceKey,
		},
		data: cacheData,
	});

	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	if (process.env.REACT_SSR) {
		const { mediaConfigEnforceDSPRecorder } = await import(
			/* webpackChunkName: "loadable-confluencessr-utilities-objectrecorder" */ '@confluence/ssr-utilities/entry-points/ObjectRecorder.ts'
		);
		mediaConfigEnforceDSPRecorder.set(!isActionEnabledForContent?.allowed);
	}
};

export const preloadIsAttachmentDownloadEnabled = async (
	contentId: string,
	spaceKey: string,
): Promise<{
	data: graphqlHelpersDSPIsActionEnabledForContentQuery$data | null;
	errors?: ReadonlyArray<GraphQLError>;
	hasErrors: boolean;
}> => {
	const action = getAttachmentDownloadDspAction();

	const response = await query<
		graphqlHelpersDSPIsActionEnabledForContentQuery$data,
		graphqlHelpersDSPIsActionEnabledForContentQuery$variables
	>({
		query: graphqlHelpersDSPIsActionEnabledForContentQuery,
		variables: {
			action,
			contentId,
			contentStatus: 'CURRENT',
			contentVersion: 1,
			spaceKey,
		},
	});

	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	if (process.env.REACT_SSR) {
		const { mediaConfigEnforceDSPRecorder } = await import(
			/* webpackChunkName: "loadable-confluencessr-utilities-objectrecorder" */ '@confluence/ssr-utilities/entry-points/ObjectRecorder.ts'
		);
		const isAttachmentDownloadAllowed =
			response?.data?.dataSecurityPolicy?.isActionEnabledForContent?.allowed || false;
		// When attachment download is not allowed, enforce the data security policy
		mediaConfigEnforceDSPRecorder.set(!isAttachmentDownloadAllowed);
	}

	return response;
};
